import { useTranslation, Trans } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/integrations/forms/shared';
import type { WizardDraft } from './state';
import { validateTarget } from './state';

interface Props {
  draft: WizardDraft;
  onChange: (patch: Partial<WizardDraft>) => void;
}

export function Step4Target({ draft, onChange }: Props): JSX.Element {
  const { t } = useTranslation();
  const errors = validateTarget(draft).errors;
  const varList = draft.fields.map((f) => `{{${f.name}}}`).join(', ');
  const placeholders = varList || t('tools.wizard.step4.noFields');

  if (draft.kind === 'elasticsearch') {
    return (
      <div className="space-y-3">
        <Field
          label={t('tools.wizard.step4.index')}
          htmlFor="w-index"
          required
          hint={t('tools.wizard.step4.indexHint')}
        >
          <Input
            id="w-index"
            value={draft.index}
            onChange={(e) => onChange({ index: e.target.value })}
            className="font-mono text-xs"
          />
        </Field>
        <Field
          label={t('tools.wizard.step4.maxHits')}
          htmlFor="w-maxhits"
          hint={t('tools.wizard.step4.maxHitsHint')}
        >
          <Input
            id="w-maxhits"
            type="number"
            value={draft.maxHits}
            onChange={(e) => onChange({ maxHits: e.target.value })}
          />
        </Field>
        <Field
          label={t('tools.wizard.step4.templateDsl')}
          htmlFor="w-tpl-json"
          required
          hint={t('tools.wizard.step4.placeholdersHint', { placeholders })}
        >
          <Textarea
            id="w-tpl-json"
            value={draft.templateJson}
            onChange={(e) => onChange({ templateJson: e.target.value })}
            rows={14}
            className="font-mono text-[11px]"
          />
        </Field>
        {errors.target ? <p className="text-[11px] text-destructive">{errors.target}</p> : null}
        {errors.template ? <p className="text-[11px] text-destructive">{errors.template}</p> : null}
        <p className="text-[11px] text-muted-foreground">
          <Trans i18nKey="tools.wizard.step4.templateEsHelp" components={{ code: <code /> }} />
        </p>
      </div>
    );
  }

  if (draft.kind === 'bigquery') {
    const bqVarList = draft.fields.map((f) => `@${f.name}`).join(', ') || t('tools.wizard.step4.noFields');
    return (
      <div className="space-y-3">
        <Field
          label={t('tools.wizard.step4.table')}
          htmlFor="w-table"
          required
          hint={t('tools.wizard.step4.tableHint')}
        >
          <Input
            id="w-table"
            value={draft.table}
            onChange={(e) => onChange({ table: e.target.value })}
            placeholder={t('tools.wizard.step4.tablePlaceholder')}
            className="font-mono text-xs"
          />
        </Field>
        <Field
          label={t('tools.wizard.step4.location')}
          htmlFor="w-loc"
          hint={t('tools.wizard.step4.locationHint')}
        >
          <Input id="w-loc" value={draft.location} onChange={(e) => onChange({ location: e.target.value })} />
        </Field>
        <Field
          label={t('tools.wizard.step4.templateSql')}
          htmlFor="w-tpl-sql"
          required
          hint={t('tools.wizard.step4.bindHint', { placeholders: bqVarList })}
        >
          <Textarea
            id="w-tpl-sql"
            value={draft.templateSql}
            onChange={(e) => onChange({ templateSql: e.target.value })}
            rows={14}
            className="font-mono text-[11px]"
          />
        </Field>
        {errors.target ? <p className="text-[11px] text-destructive">{errors.target}</p> : null}
        {errors.template ? <p className="text-[11px] text-destructive">{errors.template}</p> : null}
        <p className="text-[11px] text-muted-foreground">{t('tools.wizard.step4.templateBqHelp')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Field
        label={t('tools.wizard.step4.apiPath')}
        htmlFor="w-api-path"
        required
        hint={t('tools.wizard.step4.apiPathHint', { placeholders })}
      >
        <Input
          id="w-api-path"
          value={draft.apiPath}
          onChange={(e) => onChange({ apiPath: e.target.value })}
          placeholder="/users/{{user_id}}"
          className="font-mono text-xs"
        />
      </Field>

      <Field label={t('tools.wizard.step4.apiMethod')} htmlFor="w-api-method" required>
        <select
          id="w-api-method"
          value={draft.apiMethod}
          onChange={(e) => onChange({ apiMethod: e.target.value as WizardDraft['apiMethod'] })}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="GET">GET — {t('tools.wizard.step4.methodGet')}</option>
          <option value="POST">POST — {t('tools.wizard.step4.methodPost')}</option>
        </select>
      </Field>

      <Field
        label={t('tools.wizard.step4.apiQueryParams')}
        htmlFor="w-api-query"
        hint={t('tools.wizard.step4.apiQueryParamsHint')}
      >
        <Textarea
          id="w-api-query"
          value={draft.apiQueryTemplate}
          onChange={(e) => onChange({ apiQueryTemplate: e.target.value })}
          rows={3}
          placeholder={'{"page": "{{page}}", "limit": "10"}'}
          className="font-mono text-[11px]"
        />
      </Field>

      {draft.apiMethod !== 'GET' && (
        <Field
          label={t('tools.wizard.step4.apiBody')}
          htmlFor="w-api-body"
          hint={t('tools.wizard.step4.apiBodyHint')}
        >
          <Textarea
            id="w-api-body"
            value={draft.apiBodyTemplate}
            onChange={(e) => onChange({ apiBodyTemplate: e.target.value })}
            rows={8}
            placeholder={'{\n  "name": "{{name}}",\n  "email": "{{email}}"\n}'}
            className="font-mono text-[11px]"
          />
        </Field>
      )}

      <Field
        label={t('tools.wizard.step4.apiExtraHeaders')}
        htmlFor="w-api-headers"
        hint={t('tools.wizard.step4.apiExtraHeadersHint')}
      >
        <Textarea
          id="w-api-headers"
          value={draft.apiHeaders}
          onChange={(e) => onChange({ apiHeaders: e.target.value })}
          rows={3}
          placeholder={'{"X-Custom": "value"}'}
          className="font-mono text-[11px]"
        />
      </Field>

      <Field
        label={t('tools.wizard.step4.apiInstructions')}
        htmlFor="w-api-instructions"
        hint={t('tools.wizard.step4.apiInstructionsHint')}
      >
        <Textarea
          id="w-api-instructions"
          value={draft.aiInstructions}
          onChange={(e) => onChange({ aiInstructions: e.target.value })}
          rows={4}
          placeholder={t('tools.wizard.step4.apiInstructionsPlaceholder')}
        />
      </Field>

      <Field label={t('tools.wizard.step4.apiMaxResponse')} htmlFor="w-api-maxbytes" hint={t('tools.wizard.step4.apiMaxResponseHint')}>
        <Input
          id="w-api-maxbytes"
          type="number"
          value={draft.maxResponseBytes}
          onChange={(e) => onChange({ maxResponseBytes: e.target.value })}
        />
      </Field>

      {errors.target ? <p className="text-[11px] text-destructive">{errors.target}</p> : null}
      {errors.template ? <p className="text-[11px] text-destructive">{errors.template}</p> : null}
    </div>
  );
}
